import { DatePickerWithRange } from '@/src/shadComponents/ui/DateRangePicker';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';

import { DateRange } from 'react-day-picker';
import useJobHelpers from './hooks/useJobHelpers';
import { useEffect, useState } from 'react';
import useAccounts from '@/src/Accounts/components/hooks/useAccounts';
import { Formik, Form, Field } from 'formik';
import { format } from 'date-fns';
import MultiSelectList from '@/src/shadComponents/ui/MultiSelectList';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Textarea } from '@/src/shadComponents/ui/textarea';

const ClientRoleId = 2;

export default function JobForm({ job, onSubmit, onCancel }: any) {
    const pastMonth = job?.startDate ? new Date(job?.startDate) : new Date();
    const endDate = job?.endDate ? new Date(job?.endDate) : new Date();
    const defaultSelected: DateRange = {
        from: pastMonth,
        to: endDate,
    };
    const [range, setRange] = useState<DateRange | undefined>(defaultSelected);

    const { getJobTypes } = useJobHelpers();
    const { getAccounts } = useAccounts();

    const [jobTypes, setJobTypes] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [clients, setClients] = useState<any[]>([]);

    useEffect(() => {
        getJobTypes().then((data) => {
            if (data?.length === 0) {
                return;
            }
            setJobTypes(data);
        });

        getAccounts().then((data) => {
            if (data?.length === 0) {
                return;
            }
            setUsers(
                data
                    .filter((a: any) => a.accountRoleId !== ClientRoleId)
                    .sort((a: any, b: any) =>
                        a.firstName.localeCompare(b.firstName),
                    ),
            );

            setClients(
                data
                    .filter((a: any) => a.accountRoleId === ClientRoleId)
                    .sort((a: any, b: any) =>
                        a.firstName.localeCompare(b.firstName),
                    )
                    .map((c: any) => ({
                        label: `${c.firstName} ${c.lastName} (${c.email})`,
                        value: String(c.id),
                    })),
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Formik
            initialValues={job}
            onSubmit={async (values, actions) => {
                console.log('Saving job', values);
                onSubmit(values);
            }}
        >
            {(props) => (
                <div className="">
                    <header className="relative isolate pt-0">
                        <div
                            className="absolute inset-0 -z-10 overflow-hidden"
                            aria-hidden="true"
                        >
                            <div className="absolute left-16 top-full -mt-16 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80"></div>
                            <div className="absolute inset-x-0 bottom-0 h-px bg-gray-900/5" />
                        </div>

                        <div className="mx-auto max-w-12xl px-4 py-8 sm:px-6 lg:px-8">
                            <div className="mx-auto flex max-w-2xl items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
                                <div className="flex items-center gap-x-6">
                                    <h1>
                                        <div className="text-sm leading-6 text-muted-foreground">
                                            <span className="text-foreground">
                                                Create
                                            </span>
                                        </div>
                                        <div className="mt-1 text-2xl font-bold tracking-tight text-foreground">
                                            Add A Job
                                        </div>
                                    </h1>
                                </div>
                            </div>
                        </div>
                    </header>
                    <Form
                        className="mt-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        <div className="space-y-12">
                            <div className="grid grid-cols-1 gap-x-8 gap-y-10  pb-12 md:grid-cols-3">
                                <div className="px-8">
                                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                        Basic Details
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                                        Basic information about the job.
                                    </p>
                                </div>

                                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 p-4 sm:p-0">
                                    <div className="sm:col-span-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Name</Label>
                                            <Input
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={props.values.name}
                                                onChange={props.handleChange}
                                                onBlur={props.handleBlur}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-span-full">
                                        <div className="space-y-2">
                                            <Label>Job Lead</Label>
                                            <div>
                                                <Select
                                                    value={
                                                        props.values.ownerId ||
                                                        undefined
                                                    }
                                                    onValueChange={(value) => {
                                                        props.setFieldValue(
                                                            'ownerId',
                                                            +value,
                                                        );
                                                    }}
                                                >
                                                    <SelectTrigger className="w-[280px]">
                                                        <SelectValue placeholder="Select a job owner" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {users.map((user) => (
                                                            <SelectItem
                                                                key={`user-${user.id}`}
                                                                value={user.id}
                                                            >
                                                                {user.firstName}{' '}
                                                                {user.lastName}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="sm:col-span-4">
                                        <div className="space-y-2">
                                            <Label>Type of Event</Label>
                                            <div>
                                                <Select
                                                    value={
                                                        props.values.type ||
                                                        undefined
                                                    }
                                                    onValueChange={(value) => {
                                                        props.setFieldValue(
                                                            'type',
                                                            +value,
                                                        );
                                                    }}
                                                >
                                                    <SelectTrigger className="w-[280px]">
                                                        <SelectValue placeholder="Select an event type" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {jobTypes.map((jobType) => (
                                                            <SelectItem
                                                                key={`jobs-${jobType.id}`}
                                                                value={jobType.id}
                                                            >
                                                                {jobType.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="sm:col-span-4">
                                        <div className="space-y-2">
                                            <Label>Dates</Label>
                                            <div>
                                                <DatePickerWithRange
                                                    date={range}
                                                    setDate={(newRange: any) => {
                                                        props.setFieldValue(
                                                            'startDate',
                                                            newRange.from
                                                                ? format(
                                                                      newRange.from,
                                                                      'yyyy-MM-dd',
                                                                  )
                                                                : undefined,
                                                        );
                                                        props.setFieldValue(
                                                            'endDate',
                                                            newRange.to
                                                                ? format(
                                                                      newRange.to,
                                                                      'yyyy-MM-dd',
                                                                  )
                                                                : undefined,
                                                        );
                                                        setRange(newRange);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-span-full">
                                        <div className="space-y-2">
                                            <Label htmlFor="description">
                                                Description
                                            </Label>
                                            <Textarea
                                                id="description"
                                                name="description"
                                                rows={5}
                                                value={props.values.description}
                                                onChange={props.handleChange}
                                                onBlur={props.handleBlur}
                                            />
                                            <p className="text-sm leading-6 text-muted-foreground">
                                                Describe the basic details of the
                                                job.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 gap-x-8 gap-y-10  pb-12 md:grid-cols-3">
                                <div className="px-8">
                                    <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                        Clients
                                    </h2>
                                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                                        Add clients to this job. This will allow
                                        you to send them documents and an invite
                                        to their job portal.
                                    </p>
                                </div>

                                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 p-4 sm:p-0">
                                    <div className="col-span-full">
                                        <div className="space-y-2">
                                            <Label>Clients</Label>
                                            <MultiSelectList
                                                options={clients}
                                                values={props.values.clientIds}
                                                title="client"
                                                onChange={(value) => {
                                                    props.setFieldValue(
                                                        'clientIds',
                                                        value,
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-end gap-x-4 px-6 fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background p-3">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={onCancel}
                            >
                                Cancel
                            </Button>
                            <Button type="submit">
                                Save
                            </Button>
                        </div>
                    </Form>
                </div>
            )}
        </Formik>
    );
}
